import { DolfinParser } from './base'
import DolfinDSL from '..'
import DolfinRecord from '../../record'
import { Tables } from '../../db_types'
import { toFormulaStringLiteral } from './utils/string-literal'

export class CustomFieldParser extends DolfinParser {
  constructor(
    recordData: DolfinRecord,
    private metaColumnsCache: string[],
    private customFields: { [index: string]: Tables<'custom_fields'> },
    matcher?: RegExp
  ) {
    super(recordData, matcher, undefined)
  }

  execute(formula: string): string {
    const replacedTags: string[] = []
    const tags = this.getTags(formula)

    for (const tag of tags) {
      const matches = this.matcher!.exec(tag)

      if (matches) {
        const property = matches[3]

        // Check for recursive meta columns
        if (this.metaColumnsCache.includes(property)) {
          throw new Error('Recursion detected')
        }

        // Replace the placeholder with the actual value from the payload
        const customField = this.customFields[matches[1] ? `${matches[1]}${property}` : property]

        if (customField?.type === 'formula') {
          const parsedFormula = this.parseFormula(
            customField.formula!,
            property,
            matches[1] === 'deal.' ? 'deals' : matches[1] === 'invoice.' ? 'invoices' : undefined
          )

          replacedTags.push(`( ${parsedFormula} )`)
        } else {
          const value = this.recordData.getFieldValue(
            `${matches[1] ?? ''}$${property}`,
            this.customFields
          )

          // Text and date values must remain a single valid JavaScript literal.
          let formattedValue: string | number | boolean | null | string[] =
            value || customField?.default_value

          if ((customField?.type === 'text' || customField?.type === 'date') && tags.length > 1) {
            formattedValue = toFormulaStringLiteral(String(formattedValue ?? ''))
          } else if (customField?.type === 'number') {
            formattedValue = formattedValue || 0
          } else if (customField?.type === 'boolean') {
            formattedValue = formattedValue === 'true'
          }

          replacedTags.push(formattedValue as string)
        }
      } else {
        replacedTags.push(tag)
      }
    }

    return replacedTags.join(' ')
  }

  private parseFormula(formula: string, property: string, parentTable?: 'deals' | 'invoices') {
    const parentData =
      this.recordData.recordHasParentDeal(this.recordData.record) &&
      (parentTable === 'deals'
        ? this.recordData.getParentDeal()
        : this.recordData.getParentInvoice())

    if (!!parentTable && !parentData) {
      throw new Error(`Parent ${parentTable} not found`)
    }

    const parser = new DolfinDSL({
      formula,
      recordData: parentData
        ? new DolfinRecord(parentTable === 'deals' ? 'deal' : 'invoice', parentData)
        : this.recordData,
      metaColumnsCache: [...this.metaColumnsCache, property],
      customFields: !!parentTable
        ? Object.fromEntries(
            Object.entries(this.customFields)
              .filter(([key, _]) => /^(deal|invoice)\./.test(key))
              .map(([key, f]) => [
                key.replace(/^(deal|invoice)\./, ''),
                { ...f, name: f.name.replace(/^(deal|invoice)\./, '') }
              ])
          )
        : this.customFields,
      outcomePeriod: this.outcomePeriod,
      inFormula: true
    })

    return parser.parse()
  }
}
